import React from 'react';
import { Link } from 'react-router-dom';
import { wikiReadPath } from '../../utils/wikiFeatureFlags';
import { normalizeSpaces } from '../../utils/editorialText';

/**
 * renderTiptapDoc — minimal read-only renderer that walks a TipTap JSON
 * doc and returns React elements. Recognizes the small subset emitted by
 * the wiki maintenance / ask services: doc, paragraph, heading, bullet
 * list, list item, text, plus the `claim` mark with claimId / support /
 * citationIndexes attributes.
 *
 * Why not @tiptap/html: it's another dependency, and the doc shapes here
 * are tiny and well-known. Keeping the renderer in-tree means the same
 * Claim mark output renders in the editor (via TipTap's renderHTML) and
 * in static contexts (via this walker), with one source of truth for the
 * data attributes the citation popover hover handler reads.
 */

const claimAttrs = (mark) => {
  const attrs = mark?.attrs || {};
  const indexes = Array.isArray(attrs.citationIndexes)
    ? attrs.citationIndexes.filter(value => Number.isFinite(Number(value)))
    : [];
  const contradictionIndexes = Array.isArray(attrs.contradictionIndexes)
    ? attrs.contradictionIndexes.filter(value => Number.isFinite(Number(value)))
    : [];
  return {
    'data-claim-id': attrs.claimId || '',
    'data-support': attrs.support || 'supported',
    'data-citation-indexes': indexes.join(','),
    'data-contradiction-indexes': contradictionIndexes.join(',')
  };
};

const citationText = (indexes = []) => `[${indexes.join(',')}]`;

const safeAnchorToken = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_-]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'claim';

export const citationAnchorId = ({ claimId = '', citationIndex, fallback = '' } = {}) => (
  `wiki-cite-${safeAnchorToken(claimId || fallback)}-ref-${Number(citationIndex) || 0}`
);

export const blockAnchorId = ({ anchorId = '', id = '', blockIndex = 0, fallback = '' } = {}) => {
  const explicit = String(anchorId || id || '').trim();
  if (explicit) return explicit;
  return `wiki-block-${Number(blockIndex) || 0}${fallback ? `-${safeAnchorToken(fallback)}` : ''}`;
};

const stripModelSourceRangeCitations = (value = '') => String(value || '')
  .replace(/\s*【\s*\d+†L\d+(?:-L?\d+)?\s*】/g, '')
  .replace(/\s+([,.;:!?])/g, '$1');

const INLINE_CITATION_TOKEN_PATTERN = /\[(?:\s*\d+\s*,)*\s*\d+\s*\]?/g;

const normalizeWikiTitle = (value = '') => String(value || '')
  .replace(INLINE_CITATION_TOKEN_PATTERN, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const cleanRawWikiLinkLabel = (value = '') => String(value || '')
  .replace(INLINE_CITATION_TOKEN_PATTERN, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const pageLookupTitle = (page = {}) => normalizeWikiTitle(
  page.title || page.name || page.label || page.pageTitle || ''
);

const pageLookupId = (page = {}) => (
  page.pageId || page._id || page.id || page.targetPageId || ''
);

const resolveRawWikiLinkPage = (label = '', options = {}) => {
  if (!label) return null;
  if (typeof options.resolveWikiLinkLabel === 'function') {
    const resolved = options.resolveWikiLinkLabel(label);
    if (resolved) return resolved;
  }
  const normalized = normalizeWikiTitle(label);
  if (!normalized) return null;
  const candidates = Array.isArray(options.wikiLinkPages) ? options.wikiLinkPages : [];
  return candidates.find(page => pageLookupId(page) && pageLookupTitle(page) === normalized) || null;
};

const renderWikiLinkTarget = ({ label = '', page = null, key = '', options = {} }) => {
  const cleanLabel = cleanRawWikiLinkLabel(label);
  if (!cleanLabel) return null;
  const targetId = pageLookupId(page || {});
  const targetTitle = pageLookupTitle(page || {}) ? (page.title || page.name || cleanLabel) : cleanLabel;
  if (targetId && options.disableInternalWikiLinks) {
    return (
      <span
        key={key}
        className="wiki-internal-link wiki-internal-link--static"
        data-wiki-page-id={targetId}
        data-wiki-title={targetTitle}
        title="Private wiki backlink hidden on the public share."
      >
        {cleanLabel}
      </span>
    );
  }
  if (targetId) {
    return (
      <Link
        key={key}
        className="wiki-internal-link"
        to={wikiReadPath(targetId)}
        data-wiki-page-id={targetId}
        data-wiki-title={targetTitle}
      >
        {cleanLabel}
      </Link>
    );
  }
  return (
    <span
      key={key}
      className="wiki-internal-link wiki-internal-link--unresolved"
      data-wiki-title={cleanLabel}
    >
      {cleanLabel}
    </span>
  );
};

const renderRawWikiSyntax = (text = '', key = '', options = {}) => {
  const value = String(text || '');
  if (!value.includes('[[')) return value;
  const parts = [];
  let cursor = 0;
  let linkIndex = 0;
  while (cursor < value.length) {
    const openIndex = value.indexOf('[[', cursor);
    if (openIndex === -1) {
      parts.push(value.slice(cursor));
      break;
    }
    if (openIndex > cursor) parts.push(value.slice(cursor, openIndex));
    let closeIndex = value.indexOf(']]', openIndex + 2);
    while (closeIndex !== -1 && /\d/.test(value.charAt(closeIndex - 1))) {
      closeIndex = value.indexOf(']]', closeIndex + 1);
    }
    if (closeIndex === -1) {
      parts.push(value.slice(openIndex).replace(/\[\[|\]\]/g, ''));
      break;
    }
    const label = cleanRawWikiLinkLabel(value.slice(openIndex + 2, closeIndex));
    const page = resolveRawWikiLinkPage(label, options);
    parts.push(renderWikiLinkTarget({
      label,
      page,
      key: `${key}-raw-wiki-${linkIndex}`,
      options
    }));
    linkIndex += 1;
    cursor = closeIndex + 2;
  }
  return parts.filter(part => part !== '' && part !== null);
};

const plainText = (node) => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(plainText).join('');
  if (typeof node !== 'object') return '';
  return [node.text || '', plainText(node.content)].join('');
};

const slugifyHeading = (value = '') => {
  const slug = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
};

export const extractTocItems = (doc) => {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.content)) return [];
  const seen = new Map();
  return doc.content
    .map((node, blockIndex) => {
      if (node?.type !== 'heading') return null;
      const level = Math.max(1, Math.min(6, Number(node.attrs?.level) || 2));
      if (level !== 2 && level !== 3) return null;
      const title = plainText(node.content).trim();
      if (!title) return null;
      const base = slugifyHeading(title);
      const count = seen.get(base) || 0;
      seen.set(base, count + 1);
      return {
        id: count ? `${base}-${count + 1}` : base,
        title,
        level,
        blockIndex
      };
    })
    .filter(Boolean);
};

export const firstParagraphText = (doc) => {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.content)) return '';
  const paragraph = doc.content.find(node => node?.type === 'paragraph' && plainText(node.content).trim());
  return paragraph ? normalizeSpaces(plainText(paragraph.content)) : '';
};

const renderTextNode = (node, key, options = {}) => {
  const text = stripModelSourceRangeCitations(node?.text || '');
  if (!text) return null;
  const wikiLinkMark = Array.isArray(node.marks)
    ? node.marks.find(mark => mark?.type === 'wikiLink')
    : null;
  const markedPageId = String(wikiLinkMark?.attrs?.pageId || '');
  const validateMarkedPage = options.validateWikiLinkTargets === true && Array.isArray(options.validWikiLinkTargetIds);
  const markedPageIsLive = !validateMarkedPage || options.validWikiLinkTargetIds.some(id => String(id) === markedPageId);
  const liveMarkedPage = Array.isArray(options.wikiLinkPages)
    ? options.wikiLinkPages.find(candidate => String(pageLookupId(candidate)) === markedPageId)
    : null;
  const wikiLinkedText = markedPageId && markedPageIsLive
    ? renderWikiLinkTarget({
      label: text,
      page: liveMarkedPage || {
        pageId: markedPageId,
        title: wikiLinkMark.attrs.title || text
      },
      key: `${key}-wiki-link`,
      options
    })
    : markedPageId
      ? text
      : renderRawWikiSyntax(text, key, options);
  const claimMark = Array.isArray(node.marks)
    ? node.marks.find(mark => mark?.type === 'claim')
    : null;
  if (claimMark) {
    const attrs = claimAttrs(claimMark);
    const focused = Boolean(options.focusedClaimId)
      && attrs['data-claim-id'] === options.focusedClaimId;
    const ledgerClaim = options.claimLedgerById?.get?.(attrs['data-claim-id']);
    const retired = ledgerClaim?.checkInStatus === 'retired' || Boolean(ledgerClaim?.retiredAt);
    const indexes = attrs['data-citation-indexes'];
    const contradictionIndexes = attrs['data-contradiction-indexes'];
    const visibleIndexes = indexes || contradictionIndexes;
    const firstVisibleIndex = Number(String(visibleIndexes).split(',')[0]);
    const footnoteId = Number.isFinite(firstVisibleIndex)
      ? citationAnchorId({ claimId: attrs['data-claim-id'], citationIndex: firstVisibleIndex, fallback: key })
      : '';
    const changed = options.changedClaimIds?.has?.(attrs['data-claim-id']);
    return (
      <React.Fragment key={key}>
        <span
          ref={focused ? options.focusedClaimRef : undefined}
          className={`wiki-claim${retired ? ' wiki-claim--retired' : ''}${focused ? ' wiki-claim-citation--targeted' : ''}${changed ? ' wiki-read__changed-passage' : ''}`}
          tabIndex={focused ? -1 : undefined}
          title={retired && ledgerClaim?.retiredAt ? `Retired ${new Date(ledgerClaim.retiredAt).toLocaleDateString()}` : undefined}
          {...attrs}
        >
          {wikiLinkedText}
        </span>
        {visibleIndexes && !options.claimContinues ? (
          <sup className="wiki-claim-citation-wrap">
            <button
              type="button"
              id={footnoteId}
              className={`wiki-claim-citation${options.kinFootnote && (firstVisibleIndex ? `wiki-ref-${firstVisibleIndex}` : '') === options.kinFootnote ? ' is-kin' : ''}`}
              data-claim-id={attrs['data-claim-id']}
              data-support={attrs['data-support']}
              data-citation-indexes={indexes}
              data-contradiction-indexes={contradictionIndexes}
              data-footnote-target={firstVisibleIndex ? `wiki-ref-${firstVisibleIndex}` : ''}
              aria-label={`Backlink to source${visibleIndexes.includes(',') ? 's' : ''} ${visibleIndexes.split(',').join(', ')}`}
            >
              {citationText(visibleIndexes.split(','))}
            </button>
          </sup>
        ) : null}
      </React.Fragment>
    );
  }
  return <React.Fragment key={key}>{wikiLinkedText}</React.Fragment>;
};

const claimIdOf = node => node?.marks?.find?.(mark => mark?.type === 'claim')?.attrs?.claimId;

// A claim split across text nodes (a date, a link) cites once, after its
// last piece that shows.
const renderInline = (content = [], keyPrefix = '', options = {}) => content
  .map((child, index) => {
    if (!child) return null;
    const next = content.slice(index + 1).find(node => stripModelSourceRangeCitations(node?.text || ''));
    const claimContinues = Boolean(claimIdOf(child)) && claimIdOf(next) === claimIdOf(child);
    if (child.type === 'text') return renderTextNode(child, `${keyPrefix}-${index}`, { ...options, claimContinues });
    return null;
  })
  .filter(Boolean);

const renderPullquoteContent = (node, key, options = {}) => {
  const content = Array.isArray(node.content) ? node.content : [];
  if (!content.length && node.attrs?.text) return <p>{node.attrs.text}</p>;
  return content.map((child, index) => {
    if (child?.type === 'paragraph') return <p key={`${key}-p-${index}`}>{renderInline(child.content, `pullquote-${key}-${index}`, options)}</p>;
    if (child?.type === 'text') return renderTextNode(child, `pullquote-${key}-${index}`, options);
    return renderBlock(child, `${key}-${index}`, options);
  }).filter(Boolean);
};

const renderBlock = (node, key, options = {}) => {
  if (!node || typeof node !== 'object') return null;
  switch (node.type) {
    case 'paragraph': {
      const anchorId = blockAnchorId({
        anchorId: node.attrs?.anchorId,
        id: node.attrs?.id,
        blockIndex: key
      });
      const isRecent = options.recentAnchorIds?.has?.(anchorId);
      const className = isRecent ? 'wiki-read__paragraph--recent' : undefined;
      const children = renderInline(node.content, `p-${key}`, options);
      if (typeof options.wrapParagraph === 'function') {
        return options.wrapParagraph({
          node,
          key,
          id: anchorId,
          className,
          children
        });
      }
      return (
        <p
          key={key}
          id={anchorId}
          data-wiki-block-anchor={anchorId}
          className={className}
        >
          {children}
        </p>
      );
    }
    case 'heading': {
      // The page chrome owns the document h1. Body headings, including model-
      // generated level-1 headings, must stay subordinate for screen readers.
      const level = Math.max(2, Math.min(6, node.attrs?.level || 2));
      const HeadingTag = `h${level}`;
      const tocItem = options.tocByBlockIndex?.get?.(key);
      return <HeadingTag key={key} id={tocItem?.id}>{renderInline(node.content, `h-${key}`, options)}</HeadingTag>;
    }
    case 'bulletList':
      return (
        <ul key={key}>
          {(node.content || []).map((item, index) => renderBlock(item, index, options))}
        </ul>
      );
    case 'orderedList':
      return (
        <ol key={key}>
          {(node.content || []).map((item, index) => renderBlock(item, index, options))}
        </ol>
      );
    case 'listItem':
      return (
        <li key={key}>
          {(node.content || []).map((child, index) => renderBlock(child, index, options))}
        </li>
      );
    case 'pullquote': {
      const attribution = node.attrs?.attribution || node.attrs?.cite || '';
      return (
        <blockquote key={key} className="wiki-read-pullquote">
          {renderPullquoteContent(node, key, options)}
          {attribution ? <cite>{attribution}</cite> : null}
        </blockquote>
      );
    }
    default:
      return null;
  }
};

const renderTiptapDoc = (doc, options = {}) => {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.content)) return null;
  const tocItems = Array.isArray(options.tocItems) ? options.tocItems : [];
  const tocByBlockIndex = new Map(tocItems.map(item => [item.blockIndex, item]));
  return doc.content.map((block, index) => renderBlock(block, index, { ...options, tocByBlockIndex })).filter(Boolean);
};

export default renderTiptapDoc;
